import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateMatchScore } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { validateSetScore } from '../../utils/tennisScoring';

interface MatchScoreModalProps {
  open: boolean;
  onClose: () => void;
  match: any;
  users: any[];
  thirdSetFormat?: 'full_set' | 'match_tiebreak';
}

const MatchScoreModal = ({ open, onClose, match, users, thirdSetFormat = 'full_set' }: MatchScoreModalProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [set1Player1, setSet1Player1] = useState('');
  const [set1Player2, setSet1Player2] = useState('');
  const [set1Player1TB, setSet1Player1TB] = useState('');
  const [set1Player2TB, setSet1Player2TB] = useState('');

  const [set2Player1, setSet2Player1] = useState('');
  const [set2Player2, setSet2Player2] = useState('');
  const [set2Player1TB, setSet2Player1TB] = useState('');
  const [set2Player2TB, setSet2Player2TB] = useState('');

  const [set3Player1, setSet3Player1] = useState('');
  const [set3Player2, setSet3Player2] = useState('');
  const [set3Player1TB, setSet3Player1TB] = useState('');
  const [set3Player2TB, setSet3Player2TB] = useState('');

  const [errorMessage, setErrorMessage] = useState('');
  const [set1Error, setSet1Error] = useState('');
  const [set2Error, setSet2Error] = useState('');
  const [set3Error, setSet3Error] = useState('');

  const getUserName = (userId?: string) => {
    if (!userId) return 'TBD';
    const foundUser = users?.find((u) => u.id === userId);
    return foundUser?.name || 'Unknown';
  };

  const isPlayer = user && (match.player1_id === user.userId || match.player2_id === user.userId);
  const isAdmin = user?.email?.includes('admin');
  const canEditScore = isAdmin && match.status === 'completed';
  
  // Determine if Set 3 uses match tiebreak (10-point) instead of regular set
  const set3IsMatchTiebreak = thirdSetFormat === 'match_tiebreak';
  
  // Show tiebreak column only when score is 7-6 (tiebreak scenario)
  const showSet1TB = (set1Player1 === '7' && set1Player2 === '6') || (set1Player1 === '6' && set1Player2 === '7');
  const showSet2TB = (set2Player1 === '7' && set2Player2 === '6') || (set2Player1 === '6' && set2Player2 === '7');
  // For Set 3: show TB if it's a match tiebreak format OR if score is 7-6
  const showSet3TB = set3IsMatchTiebreak || (set3Player1 === '7' && set3Player2 === '6') || (set3Player1 === '6' && set3Player2 === '7');

  // Load existing scores when modal opens with completed match
  useEffect(() => {
    if (open && match.score?.sets) {
      const sets = match.score.sets;
      
      // Set 1
      if (sets[0]) {
        setSet1Player1(sets[0].player1_games?.toString() || '');
        setSet1Player2(sets[0].player2_games?.toString() || '');
        setSet1Player1TB(sets[0].player1_tiebreak?.toString() || '');
        setSet1Player2TB(sets[0].player2_tiebreak?.toString() || '');
      }
      
      // Set 2
      if (sets[1]) {
        setSet2Player1(sets[1].player1_games?.toString() || '');
        setSet2Player2(sets[1].player2_games?.toString() || '');
        setSet2Player1TB(sets[1].player1_tiebreak?.toString() || '');
        setSet2Player2TB(sets[1].player2_tiebreak?.toString() || '');
      }
      
      // Set 3
      if (sets[2]) {
        // Check if it's a match tiebreak (games = 0-0)
        if (sets[2].player1_games === 0 && sets[2].player2_games === 0) {
          // Match tiebreak - scores are in tiebreak fields
          setSet3Player1('');
          setSet3Player2('');
          setSet3Player1TB(sets[2].player1_tiebreak?.toString() || '');
          setSet3Player2TB(sets[2].player2_tiebreak?.toString() || '');
        } else {
          // Regular set
          setSet3Player1(sets[2].player1_games?.toString() || '');
          setSet3Player2(sets[2].player2_games?.toString() || '');
          setSet3Player1TB(sets[2].player1_tiebreak?.toString() || '');
          setSet3Player2TB(sets[2].player2_tiebreak?.toString() || '');
        }
      }
    } else if (open) {
      // Clear all fields when opening for a new score entry
      setSet1Player1('');
      setSet1Player2('');
      setSet1Player1TB('');
      setSet1Player2TB('');
      setSet2Player1('');
      setSet2Player2('');
      setSet2Player1TB('');
      setSet2Player2TB('');
      setSet3Player1('');
      setSet3Player2('');
      setSet3Player1TB('');
      setSet3Player2TB('');
      setErrorMessage('');
      setSet1Error('');
      setSet2Error('');
      setSet3Error('');
    }
  }, [open, match.score]);

  // Real-time validation
  useEffect(() => {
    if (set1Player1 && set1Player2) {
      const error = validateSetScore(parseInt(set1Player1), parseInt(set1Player2));
      setSet1Error(error || '');
    } else {
      setSet1Error('');
    }
  }, [set1Player1, set1Player2]);

  useEffect(() => {
    if (set2Player1 && set2Player2) {
      const error = validateSetScore(parseInt(set2Player1), parseInt(set2Player2));
      setSet2Error(error || '');
    } else {
      setSet2Error('');
    }
  }, [set2Player1, set2Player2]);

  useEffect(() => {
    if (set3Player1 && set3Player2) {
      const error = validateSetScore(parseInt(set3Player1), parseInt(set3Player2));
      setSet3Error(error || '');
    } else {
      setSet3Error('');
    }
  }, [set3Player1, set3Player2]);

  // Validate tiebreaks
  const validateTiebreak = (
    tb1: string, 
    tb2: string, 
    games1?: string, 
    games2?: string, 
    isMatchTiebreak: boolean = false
  ): string | null => {
    if (!tb1 || !tb2) return null;
    
    const score1 = parseInt(tb1);
    const score2 = parseInt(tb2);
    const higher = Math.max(score1, score2);
    const lower = Math.min(score1, score2);
    const minPoints = isMatchTiebreak ? 10 : 7;
    
    if (higher < minPoints) {
      return `Tiebreak must be won with at least ${minPoints} points`;
    }
    if (higher - lower < 2) {
      return 'Tiebreak must be won by at least 2 points';
    }
    
    // Validate that tiebreak winner matches set winner (player with 7 games)
    if (!isMatchTiebreak && games1 && games2) {
      const g1 = parseInt(games1);
      const g2 = parseInt(games2);
      
      // If either player has 7 games (7-6 or 7-5), they must have won the tiebreak
      if (g1 === 7 && g2 === 6) {
        // Player 1 won with 7-6, must have higher tiebreak score
        if (score1 <= score2) {
          return 'Player with 7 games must have won the tiebreak';
        }
      } else if (g2 === 7 && g1 === 6) {
        // Player 2 won with 6-7, must have higher tiebreak score
        if (score2 <= score1) {
          return 'Player with 7 games must have won the tiebreak';
        }
      }
    }
    
    return null;
  };

  // Validate tiebreaks in real-time
  useEffect(() => {
    if (set1Player1TB && set1Player2TB) {
      const error = validateTiebreak(set1Player1TB, set1Player2TB, set1Player1, set1Player2);
      if (error) setSet1Error(error);
    }
  }, [set1Player1TB, set1Player2TB, set1Player1, set1Player2]);

  useEffect(() => {
    if (set2Player1TB && set2Player2TB) {
      const error = validateTiebreak(set2Player1TB, set2Player2TB, set2Player1, set2Player2);
      if (error) setSet2Error(error);
    }
  }, [set2Player1TB, set2Player2TB, set2Player1, set2Player2]);

  useEffect(() => {
    if (set3Player1TB && set3Player2TB && !set3IsMatchTiebreak) {
      const error = validateTiebreak(set3Player1TB, set3Player2TB, set3Player1, set3Player2);
      if (error) setSet3Error(error);
    }
  }, [set3Player1TB, set3Player2TB, set3Player1, set3Player2, set3IsMatchTiebreak]);

  const updateMatchMutation = useMutation(
    (score: any) => updateMatchScore(match.id, score),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['match', match.id]);
        queryClient.invalidateQueries(['matches']);
        queryClient.invalidateQueries(['tournaments']);
        onClose();
      },
    }
  );

  const handleSubmit = () => {
    setErrorMessage('');
    const sets = [];

    if (set1Player1 && set1Player2) {
      const s1 = parseInt(set1Player1);
      const s2 = parseInt(set1Player2);
      const error = validateSetScore(s1, s2);
      if (error) {
        setErrorMessage(`Set 1: ${error}`);
        return;
      }
      // Validate tiebreak if present
      if (set1Player1TB && set1Player2TB) {
        const tbError = validateTiebreak(set1Player1TB, set1Player2TB, set1Player1, set1Player2);
        if (tbError) {
          setErrorMessage(`Set 1 Tiebreak: ${tbError}`);
          return;
        }
      }
      const setData: any = { set_number: 1, player1_games: s1, player2_games: s2 };
      if (set1Player1TB) setData.player1_tiebreak = parseInt(set1Player1TB);
      if (set1Player2TB) setData.player2_tiebreak = parseInt(set1Player2TB);
      sets.push(setData);
    }

    if (set2Player1 && set2Player2) {
      const s1 = parseInt(set2Player1);
      const s2 = parseInt(set2Player2);
      const error = validateSetScore(s1, s2);
      if (error) {
        setErrorMessage(`Set 2: ${error}`);
        return;
      }
      // Validate tiebreak if present
      if (set2Player1TB && set2Player2TB) {
        const tbError = validateTiebreak(set2Player1TB, set2Player2TB, set2Player1, set2Player2);
        if (tbError) {
          setErrorMessage(`Set 2 Tiebreak: ${tbError}`);
          return;
        }
      }
      const setData: any = { set_number: 2, player1_games: s1, player2_games: s2 };
      if (set2Player1TB) setData.player1_tiebreak = parseInt(set2Player1TB);
      if (set2Player2TB) setData.player2_tiebreak = parseInt(set2Player2TB);
      sets.push(setData);
    }

    // Set 3: Handle both match tiebreak and regular set
    if (set3IsMatchTiebreak) {
      // Match tiebreak: scores are in the TB fields
      if (set3Player1TB && set3Player2TB) {
        const s1 = parseInt(set3Player1TB);
        const s2 = parseInt(set3Player2TB);
        // Validate match tiebreak: first to 10, win by 2
        const higher = Math.max(s1, s2);
        const lower = Math.min(s1, s2);
        if (higher < 10) {
          setErrorMessage('Match tiebreak: Winner must reach at least 10 points');
          return;
        }
        if (higher - lower < 2) {
          setErrorMessage('Match tiebreak: Winner must win by at least 2 points');
          return;
        }
        // Store as set with games = 0 and tiebreak scores
        const setData: any = { 
          set_number: 3, 
          player1_games: 0, 
          player2_games: 0,
          player1_tiebreak: s1,
          player2_tiebreak: s2
        };
        sets.push(setData);
      }
    } else {
      // Regular set
      if (set3Player1 && set3Player2) {
        const s1 = parseInt(set3Player1);
        const s2 = parseInt(set3Player2);
        const error = validateSetScore(s1, s2);
        if (error) {
          setErrorMessage(`Set 3: ${error}`);
          return;
        }
        // Validate tiebreak if present
        if (set3Player1TB && set3Player2TB) {
          const tbError = validateTiebreak(set3Player1TB, set3Player2TB, set3Player1, set3Player2);
          if (tbError) {
            setErrorMessage(`Set 3 Tiebreak: ${tbError}`);
            return;
          }
        }
        const setData: any = { set_number: 3, player1_games: s1, player2_games: s2 };
        if (set3Player1TB) setData.player1_tiebreak = parseInt(set3Player1TB);
        if (set3Player2TB) setData.player2_tiebreak = parseInt(set3Player2TB);
        sets.push(setData);
      }
    }

    if (sets.length < 2) {
      setErrorMessage('You must enter scores for at least Set 1 and Set 2');
      return;
    }

    let player1Sets = 0;
    let player2Sets = 0;
    sets.forEach((set) => {
      // For match tiebreak (games = 0-0), compare tiebreak scores
      if (set.player1_games === 0 && set.player2_games === 0 && set.player1_tiebreak && set.player2_tiebreak) {
        if (set.player1_tiebreak > set.player2_tiebreak) player1Sets++;
        else if (set.player2_tiebreak > set.player1_tiebreak) player2Sets++;
      } else {
        // Regular set: compare games
        if (set.player1_games > set.player2_games) player1Sets++;
        else if (set.player2_games > set.player1_games) player2Sets++;
      }
    });

    if (player1Sets < 2 && player2Sets < 2) {
      setErrorMessage('Invalid match result: One player must win at least 2 sets. Please enter Set 3 scores.');
      return;
    }

    // UpdateScore handler automatically calculates winner and sets status to completed
    updateMatchMutation.mutate({ sets });
  };

  // Display completed match score
  if (match.status === 'completed' && match.score && !canEditScore) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Match Score</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Player</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Set 1</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Set 2</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Set 3</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: match.winner_id === match.player1_id ? 'success.50' : 'inherit' }}>
                  <TableCell sx={{ fontWeight: match.winner_id === match.player1_id ? 600 : 400 }}>
                    {getUserName(match.player1_id)}
                  </TableCell>
                  {match.score.sets?.map((set: any, idx: number) => (
                    <TableCell key={idx} align="center">
                      {set.player1_games}
                      {set.player1_tiebreak !== undefined && <sup>{set.player1_tiebreak}</sup>}
                    </TableCell>
                  ))}
                  {match.score.sets?.length < 3 && <TableCell align="center">-</TableCell>}
                </TableRow>
                <TableRow sx={{ bgcolor: match.winner_id === match.player2_id ? 'success.50' : 'inherit' }}>
                  <TableCell sx={{ fontWeight: match.winner_id === match.player2_id ? 600 : 400 }}>
                    {getUserName(match.player2_id)}
                  </TableCell>
                  {match.score.sets?.map((set: any, idx: number) => (
                    <TableCell key={idx} align="center">
                      {set.player2_games}
                      {set.player2_tiebreak !== undefined && <sup>{set.player2_tiebreak}</sup>}
                    </TableCell>
                  ))}
                  {match.score.sets?.length < 3 && <TableCell align="center">-</TableCell>}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Score entry form
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Enter Match Score</DialogTitle>
      <DialogContent>
        {!isPlayer && isAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You are entering the score as an administrator
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 150 }}>Player</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Set 1</TableCell>
                {showSet1TB && <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>TB</TableCell>}
                <TableCell align="center" sx={{ fontWeight: 600 }}>Set 2</TableCell>
                {showSet2TB && <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>TB</TableCell>}
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {set3IsMatchTiebreak ? 'Match TB' : 'Set 3'}
                </TableCell>
                {showSet3TB && !set3IsMatchTiebreak && <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>TB</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Player 1 Row */}
              <TableRow>
                <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 500 }}>
                  {getUserName(match.player1_id)}
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={set1Player1}
                    onChange={(e) => setSet1Player1(e.target.value)}
                    size="small"
                    error={!!set1Error}
                    inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                  />
                </TableCell>
                {showSet1TB && (
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set1Player1TB}
                      onChange={(e) => setSet1Player1TB(e.target.value)}
                      size="small"
                      inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={set2Player1}
                    onChange={(e) => setSet2Player1(e.target.value)}
                    size="small"
                    error={!!set2Error}
                    inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                  />
                </TableCell>
                {showSet2TB && (
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set2Player1TB}
                      onChange={(e) => setSet2Player1TB(e.target.value)}
                      size="small"
                      inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={set3IsMatchTiebreak ? set3Player1TB : set3Player1}
                    onChange={(e) => set3IsMatchTiebreak ? setSet3Player1TB(e.target.value) : setSet3Player1(e.target.value)}
                    size="small"
                    error={!!set3Error}
                    placeholder={set3IsMatchTiebreak ? '10' : ''}
                    inputProps={{ min: 0, max: set3IsMatchTiebreak ? 99 : 7, style: { textAlign: 'center', width: 40 } }}
                  />
                </TableCell>
                {showSet3TB && !set3IsMatchTiebreak && (
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set3Player1TB}
                      onChange={(e) => setSet3Player1TB(e.target.value)}
                      size="small"
                      inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                )}
              </TableRow>

              {/* Player 2 Row */}
              <TableRow>
                <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 500 }}>
                  {getUserName(match.player2_id)}
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={set1Player2}
                    onChange={(e) => setSet1Player2(e.target.value)}
                    size="small"
                    error={!!set1Error}
                    inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                  />
                </TableCell>
                {showSet1TB && (
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set1Player2TB}
                      onChange={(e) => setSet1Player2TB(e.target.value)}
                      size="small"
                      inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={set2Player2}
                    onChange={(e) => setSet2Player2(e.target.value)}
                    size="small"
                    error={!!set2Error}
                    inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                  />
                </TableCell>
                {showSet2TB && (
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set2Player2TB}
                      onChange={(e) => setSet2Player2TB(e.target.value)}
                      size="small"
                      inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={set3IsMatchTiebreak ? set3Player2TB : set3Player2}
                    onChange={(e) => set3IsMatchTiebreak ? setSet3Player2TB(e.target.value) : setSet3Player2(e.target.value)}
                    size="small"
                    error={!!set3Error}
                    placeholder={set3IsMatchTiebreak ? '10' : ''}
                    inputProps={{ min: 0, max: set3IsMatchTiebreak ? 99 : 7, style: { textAlign: 'center', width: 40 } }}
                  />
                </TableCell>
                {showSet3TB && !set3IsMatchTiebreak && (
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set3Player2TB}
                      onChange={(e) => setSet3Player2TB(e.target.value)}
                      size="small"
                      inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {(set1Error || set2Error || set3Error) && (
          <Box sx={{ mt: 2 }}>
            {set1Error && <Typography variant="caption" color="error" display="block">Set 1: {set1Error}</Typography>}
            {set2Error && <Typography variant="caption" color="error" display="block">Set 2: {set2Error}</Typography>}
            {set3Error && <Typography variant="caption" color="error" display="block">Set 3: {set3Error}</Typography>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!set1Player1 || !set1Player2 || !set2Player1 || !set2Player2 || !!set1Error || !!set2Error || !!set3Error}
        >
          Submit Score
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatchScoreModal;
